import { useAuth, useUser } from '@clerk/clerk-react';
import { useSubscription as useClerkBillingSubscription } from '@clerk/clerk-react/experimental';
import { useMemo } from 'react';
import { SubscriptionTier, normalizeTier, getSubscriptionInfo, hasTierAccess } from '../types/subscription';

export interface UseSubscriptionReturn {
  tier: SubscriptionTier;
  subscriptionInfo: ReturnType<typeof getSubscriptionInfo>;
  isLoading: boolean;
  hasAccess: (requiredTier: SubscriptionTier) => boolean;
  isFree: boolean;
  isPersonal: boolean;
  isProfessional: boolean;
}

const TIER_RANK: Record<SubscriptionTier, number> = { free: 0, personal: 1, professional: 2 };

/**
 * Hook to get user's subscription tier from Clerk Billing.
 * Primary source: Clerk Billing subscription (plan.name).
 * Fallback: publicMetadata / unsafeMetadata keys.
 * Unauthenticated users get 'free'.
 */
export function useSubscription(): UseSubscriptionReturn {
  const { isLoaded: authLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { data: clerkSubscription, isLoading: billingLoading } = useClerkBillingSubscription();

  const isLoading = !authLoaded || !userLoaded || billingLoading;

  const tier = useMemo((): SubscriptionTier => {
    if (!user) return 'free';

    let best: SubscriptionTier = 'free';

    const consider = (value: string | null | undefined) => {
      if (!value) return;
      const t = normalizeTier(value);
      if (TIER_RANK[t] > TIER_RANK[best]) best = t;
    };

    // 1) Primary: Clerk Billing subscription → subscriptionItems[].plan.name
    const activeItem = clerkSubscription?.subscriptionItems?.find(
      (item) => item.status === 'active'
    ) ?? clerkSubscription?.subscriptionItems?.[0];
    if (activeItem?.plan?.name) {
      consider(activeItem.plan.name);
    }

    // 2) Fallback: metadata keys (for cases where billing isn't configured)
    if (best === 'free') {
      const publicMeta = user.publicMetadata as Record<string, unknown> | undefined;
      const unsafeMeta = user.unsafeMetadata as Record<string, unknown> | undefined;

      for (const meta of [publicMeta, unsafeMeta]) {
        if (!meta) continue;
        consider(meta.subscriptionTier as string | undefined);
        consider(meta.subscriptionPlan as string | undefined);
        consider(meta.plan as string | undefined);
        consider(meta.subscription_plan as string | undefined);
        consider(meta.tier as string | undefined);
        consider((meta.plan as { name?: string })?.name);
        consider((meta.subscriptionPlan as { name?: string })?.name);
        consider((meta.subscription as { tier?: string; plan?: string })?.tier);
        consider((meta.subscription as { tier?: string; plan?: string })?.plan);
        consider((meta.billing as { plan?: string })?.plan);
      }
    }

    return best;
  }, [user, clerkSubscription]);

  const subscriptionInfo = useMemo(() => getSubscriptionInfo(tier), [tier]);

  const hasAccess = useMemo(() => {
    return (requiredTier: SubscriptionTier) => hasTierAccess(tier, requiredTier);
  }, [tier]);

  return {
    tier,
    subscriptionInfo,
    isLoading,
    hasAccess,
    isFree: tier === 'free',
    isPersonal: tier === 'personal',
    isProfessional: tier === 'professional',
  };
}
